import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Condition, ConditionGroup, Dimension, FulfillmentsData, Metric, SyntaxchecksData, Timing } from "./AppTypes";
import { makeId } from "./xmlHandling";
import { notifyProjectActivity } from "./api/pingClient";
import { createPortal } from "react-dom";
import {
  applySingleVisual,
  getBaseColor,
  getFulfillmentPalette,
  getFulfillmentIndicator,
  colorFromFulfillment,
  resolveFulfillmentTone,
  FULFILLMENT_STEPS,
  Mode,
  FulfillmentTone,
} from "./colorUtils";

// Muss mit backend/settings.py SPECIAL_CHARACTERS synchron bleiben
const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

type DimRefRole = "for" | "where" | "on";

/* ---------- Types ---------- */

/**
 * Erweiterter Condition-Typ, wie er im Editor verwendet wird:
 * - enthält zusätzlich:
 *   - groupId: zu welcher Gruppe die Condition gehört
 *   - dimrefs: Dimensionen, auf die sich die Bedingung bezieht (inkl. Rolle und Modus)
 *   - patternType/patternValue: Art des Vergleichs (gleich, Werte-Liste, Range, …)
 * 
 * WICHTIG: ExtendedCondition verwendet eine UI-spezifische dimrefs-Struktur
 * mit id, dimensionId, mode, tagIds, pointIds (für Drag & Drop und UI-Steuerung).
 * Die base Condition hat dimrefs?: DimRef[] (solver-kompatibel).
 */
export type ExtendedCondition = Omit<Condition, "dimrefs" | "participants" | "on"> & {
  groupId: string;
  dimrefs: {
    id: string;
    dimensionId: string;
    role: "for" | "where" | "on";
    mode: "all" | "tags" | "points";
    tagIds: string[];
    pointIds: string[];
  }[];
  patternType: "equal" | "not_equal" | "min" | "max";
  patternValue: string;
  // align condition (UI-spezifische Struktur)
  participants?: AlignParticipantExt[];
  on?: AlignOnExt;
  // follow condition (metric_ref IF/THEN)
  metricRefIf?: { name: string; patternType: "equal" | "not_equal" | "min" | "max"; patternValue: string };
  metricRefThen?: { name: string; patternType: "equal" | "not_equal" | "min" | "max"; patternValue: string };
  scope?: "always" | "template" | "period" | "day" | "repair";
  mode?: "any" | "shortterm" | "longterm";
};

type DimRefExt = ExtendedCondition["dimrefs"][number];
type AlignParticipantExt = {
  id: string;
  dimrefs: DimRefExt[];
};
type AlignOnExt = {
  dimrefs: DimRefExt[];
};

/**
 * Props für den ConditionEditor:
 * - conditions: alle Bedingungen (ExtendedCondition)
 * - groups: Gruppen, in denen Bedingungen organisiert sind
 * - onChange: Callback, wenn sich Conditions oder Groups ändern
 * - dimensions: verfügbare Dimensionen aus dem Datenmodell
 * - allNames: Liste aller Condition-Namen (für Duplikatserkennung)
 * - isActive: ob der Editor aufgeklappt ist
 * - toggle: ein/ausklappen des Editors
 * - userHue/mode: Farbschema
 * - statsData: optionale Statistikdaten (Fulfillment, …)
 * - syntaxData: optionale Syntax-Check-Daten (syntax, message, …)
 */
type Props = {
  conditions: ExtendedCondition[];
  groups: ConditionGroup[];
  onChange: (conds: ExtendedCondition[], groups: ConditionGroup[]) => void;
  dimensions: Dimension[];
  timings: Timing[];
  allNames: string[];
  isActive: boolean;
  toggle: () => void;
  userHue: number;
  mode: Mode;
  statsData?: FulfillmentsData | null;
  syntaxData?: SyntaxchecksData | null;
  projectId: number;
  metrics?: Metric[];
};

/* ---------- Context ---------- */

/**
 * Kontext-Typ, der von allen Unterkomponenten genutzt wird.
 * Der Context kapselt:
 * - Daten (dimensions, userHue, mode, allNames, groups)
 * - abgeleitete Daten (conditionStatsMap, conditionSyntaxMap)
 * - Mutations-Funktionen (updateCondition, deleteCondition, updateGroup, deleteGroup, addCondition)
 * - UI-Status (expandedId, editingGroupId, editingGroupName, collapsedGroups, hoverGroupId)
 */
type EditorContextType = {
  dimensions: Dimension[];
  timings: Timing[];
  metrics?: Metric[];
  userHue: number;
  mode: Mode;
  allNames: string[];
  conditionStatsMap: Map<string, any>;
  conditionSyntaxMap: Map<string, any>;
  updateCondition: (id: string, updates: Partial<ExtendedCondition>) => void;
  deleteCondition: (id: string) => void;
  updateGroup: (id: string, updates: Partial<ConditionGroup>) => boolean;
  deleteGroup: (id: string) => void;
  addCondition: (groupId: string) => void;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  editingGroupId: string | null;
  setEditingGroupId: (id: string | null) => void;
  editingGroupName: string;
  setEditingGroupName: (name: string) => void;
  collapsedGroups: Set<string>;
  toggleGroupCollapse: (id: string) => void;
  hoverGroupId: string | null;
  groups: ConditionGroup[];
  conditions: ExtendedCondition[];
  onChange: (conds: ExtendedCondition[], groups: ConditionGroup[]) => void;
};

// React-Context für den Editor
const EditorContext = createContext<EditorContextType | null>(null);

/**
 * Custom Hook zum Zugriff auf den EditorContext.
 * Stellt sicher, dass der Hook nur innerhalb des Providers verwendet wird.
 */
const useEditorContext = () => {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error("useEditorContext must be used within Provider");
  return ctx;
};

/* ---------- Helpers ---------- */

function RolePicker({
  value,
  onChange,
  allowFocus,
  uiMode,
}: {
  value: DimRefRole;
  onChange: (role: DimRefRole) => void;
  allowFocus?: boolean;
  uiMode: Mode;
}) {
  const bg = uiMode === "dark" ? "rgba(20, 20, 24, 0.85)" : "rgba(255,255,255,0.75)";
  const border = uiMode === "dark" ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)";
  const shadow = uiMode === "dark" ? "0 10px 22px rgba(0,0,0,0.55)" : "0 6px 18px rgba(0,0,0,0.18)";
  return (
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 8px",
      borderRadius: 8,
      background: bg,
      border,
      boxShadow: shadow,
      backdropFilter: "blur(2px)",
    }}>
      <span style={{ fontSize: 12, opacity: 0.85 }}>Als:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as DimRefRole)}
        style={{ padding: "4px 6px" }}
      >
        <option value="where">where</option>
        <option value="for">for</option>
        {allowFocus ? <option value="on">on</option> : null}
      </select>
    </div>
  );
}

/**
 * Bestimmt die Hintergrundfarbe einer Condition:
 * - wenn enforcement "off": grau (deaktiviert)
 * - wenn Syntax-Fehler vorhanden: blau (Warning/Error)
 * - wenn Fulfillment vorhanden: Farbe gemäß Fulfillment
 * - sonst: Basisfarbe je nach Condition-Typ
 */
function getConditionColor(cond: ExtendedCondition, stat: any, syntaxStat: any, hue: number, mode: Mode) {
  // Wenn enforcement "off", dann weiche graue Farbe mit leichtem Farbton
  if (cond.enforcement === "off") {
    return mode === "dark"
      ? "hsl(220, 10%, 25%)"  // Dunkelgrau mit leichtem Blaustich
      : "hsl(220, 15%, 88%)"; // Hellgrau mit leichtem Blaustich
  }

  // Syntax Warning/Error -> Blue
  if (syntaxStat?.SyntaxMessages && syntaxStat.SyntaxMessages.length > 0) {
    return mode === "dark"
      ? "hsl(210, 50%, 30%)"
      : "hsl(210, 95%, 82%)";
  }

  let color;
  const fulfillment = stat?.Fulfillment;

  if (typeof fulfillment === "number" && !Number.isNaN(fulfillment)) {
    color = getFulfillmentPalette(resolveFulfillmentTone(fulfillment).tone as any, mode).background;
  } else {
    color = getBaseColor(3, cond.type, hue, mode);
  }

  // Bei enforcement "hard" erhöhen wir die Sättigung und den Kontrast
  if (cond.enforcement === "hard") {
    // 8 entspricht +80% Chroma - deutlich kräftigere Farben
    let c = applySingleVisual(color, { type: "chroma", value: 8 }, mode);
    // Zusätzlich 5% mehr Kontrast (dunkler in Light Mode, heller in Dark Mode)
    c = applySingleVisual(c, { type: "lightness", value: 5 }, mode);
    return c;
  }

  return color;
}

/* ---------- Components ---------- */


// Reusable Drop Zone Component (extracted)
const AlignDropZone = ({
  title,
  dimrefs,
  onDeleteDimref,
  onUpdateDimref,
  onReorderDimrefs,
  onReorderPoint,
  onReorderTag,
  zoneType,
  zoneId,
  canRemoveZone,
  onRemoveZone,
  handleDrop,
  mode,
  dimensions,
  condId,
  droppedInZoneRef
}: {
  title: React.ReactNode,
  dimrefs: DimRefExt[],
  onDeleteDimref: (id: string, pointId?: string, tagId?: string) => void,
  onUpdateDimref: (id: string, updates: Partial<DimRefExt>) => void,
  onReorderDimrefs?: (sourceDimrefId: string, targetDimrefId: string, zoneType: "participant" | "on", zoneId?: string) => void,
  onReorderPoint?: (dimrefId: string, fromPointId: string, toPointId: string, zoneType: "participant" | "on", zoneId?: string) => void,
  onReorderTag?: (dimrefId: string, fromTagId: string, toTagId: string, zoneType: "participant" | "on", zoneId?: string) => void,
  zoneType: "participant" | "on",
  zoneId?: string,
  canRemoveZone?: boolean,
  onRemoveZone?: () => void,
  handleDrop: (e: React.DragEvent<HTMLDivElement>, type: "participant" | "on", id?: string) => void,
  mode: string,
  dimensions: Dimension[],
  condId: string,
  droppedInZoneRef: React.MutableRefObject<boolean>,
  key?: React.Key
}) => {
  return (
    <div
      data-align-dropzone="1"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("application/x-zeitgeist") || e.dataTransfer.types.includes("application/x-dimref-delete")) {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.style.borderColor = "#4ade80";
          e.currentTarget.style.background = mode === "dark" ? "rgba(74, 222, 128, 0.12)" : "rgba(74, 222, 128, 0.08)";
        }
      }}
      onDragLeave={(e) => {
        e.stopPropagation();
        e.currentTarget.style.borderColor = mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
        e.currentTarget.style.background = mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";
      }}
      onDrop={(e) => {
        e.stopPropagation();
        handleDrop(e, zoneType, zoneId);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        minHeight: 36,
        border: mode === "dark" ? "1px dashed rgba(255,255,255,0.1)" : "1px dashed rgba(0,0,0,0.08)",
        borderRadius: 8,
        background: mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
        transition: "all 0.15s ease",
        flexWrap: "wrap",
        position: "relative" // Ensure positioning context
      }}
    >
      <span style={{
        fontSize: "0.7em",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.65)",
        minWidth: 42,
        flexShrink: 0,
        marginRight: 6
      }}>
        {title}
      </span>

      {/* Chips */}
      {dimrefs.map((dr) => {
        const dim = dimensions.find((d) => d.id === dr.dimensionId);
        if (!dim) return null;

        const buttonStyle: React.CSSProperties = {
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "4px 10px",
          background: mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          borderRadius: 14,
          fontSize: "14px",
          fontWeight: 500,
          color: mode === "dark" ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)",
          cursor: "grab",
          userSelect: "none",
          transition: "all 0.15s ease",
        };

        // Points
        if (dr.mode === "points" && dr.pointIds?.length) {
          return dr.pointIds.map(pid => {
            const pt = dim.points.find((p: any) => p.id === pid);
            if (!pt) return null;
            return (
              <div
                key={`${dr.id}-${pid}`}
                draggable
                onDragStart={(e) => {
                  droppedInZoneRef.current = false;
                  e.dataTransfer.setData("application/x-dimref-delete", JSON.stringify({ type: "point", dimrefId: dr.id, pointId: pid, condId: condId }));
                  e.dataTransfer.effectAllowed = "move";
                  (e.currentTarget as HTMLElement).style.opacity = "0.5";
                }}
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes("application/x-dimref-delete")) {
                    e.preventDefault();
                    e.stopPropagation();
                    (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                    (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                  }
                }}
                onDragLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.outline = "";
                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                }}
                onDrop={(e) => {
                  (e.currentTarget as HTMLElement).style.outline = "";
                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                  droppedInZoneRef.current = true;
                  const raw = e.dataTransfer.getData("application/x-dimref-delete");
                  if (!raw) return;
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const payload = JSON.parse(raw);
                    if (payload.condId !== condId) return;
                    if (payload.type === "point" && payload.pointId) {
                      if (payload.dimrefId === dr.id) {
                        onReorderPoint?.(dr.id, payload.pointId, pid, zoneType, zoneId);
                      } else {
                        onReorderDimrefs?.(payload.dimrefId, dr.id, zoneType, zoneId);
                      }
                      return;
                    }
                    if (payload.type === "dimension") {
                      onReorderDimrefs?.(payload.dimrefId, dr.id, zoneType, zoneId);
                    }
                  } catch (err) {
                    console.error("Reorder drop error:", err);
                  }
                }}
                onDragEnd={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "1";
                  if (!droppedInZoneRef.current) {
                    onDeleteDimref(dr.id, pid);
                  }
                }}
                style={buttonStyle}
                title="Drag out to remove"
              >
                {pt.name}
              </div>
            );
          });
        }
        // Tags
        if (dr.mode === "tags" && dr.tagIds?.length) {
          return dr.tagIds.map(tid => {
            const tag = dim.tags.find((t: any) => t.id === tid);
            if (!tag) return null;
            return (
              <div
                key={`${dr.id}-${tid}`}
                draggable
                onDragStart={(e) => {
                  droppedInZoneRef.current = false;
                  e.dataTransfer.setData("application/x-dimref-delete", JSON.stringify({ type: "tag", dimrefId: dr.id, tagId: tid, condId: condId }));
                  e.dataTransfer.effectAllowed = "move";
                  (e.currentTarget as HTMLElement).style.opacity = "0.5";
                }}
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes("application/x-dimref-delete")) {
                    e.preventDefault();
                    e.stopPropagation();
                    (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                    (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                  }
                }}
                onDragLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.outline = "";
                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                }}
                onDrop={(e) => {
                  (e.currentTarget as HTMLElement).style.outline = "";
                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                  droppedInZoneRef.current = true;
                  const raw = e.dataTransfer.getData("application/x-dimref-delete");
                  if (!raw) return;
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const payload = JSON.parse(raw);
                    if (payload.condId !== condId) return;
                    if (payload.type === "tag" && payload.tagId) {
                      if (payload.dimrefId === dr.id) {
                        onReorderTag?.(dr.id, payload.tagId, tid, zoneType, zoneId);
                      } else {
                        onReorderDimrefs?.(payload.dimrefId, dr.id, zoneType, zoneId);
                      }
                      return;
                    }
                    if (payload.type === "dimension") {
                      onReorderDimrefs?.(payload.dimrefId, dr.id, zoneType, zoneId);
                    }
                  } catch (err) {
                    console.error("Reorder drop error:", err);
                  }
                }}
                onDragEnd={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = "1";
                  if (!droppedInZoneRef.current) {
                    onDeleteDimref(dr.id, undefined, tid);
                  }
                }}
                style={buttonStyle}
                title="Drag out to remove"
              >
                {tag.name}
              </div>
            );
          });
        }
        // All
        return (
          <div
            key={dr.id}
            draggable
            onDragStart={(e) => {
              droppedInZoneRef.current = false;
              e.dataTransfer.setData("application/x-dimref-delete", JSON.stringify({ type: "dimension", dimrefId: dr.id, condId: condId }));
              e.dataTransfer.effectAllowed = "move";
              (e.currentTarget as HTMLElement).style.opacity = "0.5";
            }}
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes("application/x-dimref-delete")) {
                e.preventDefault();
                e.stopPropagation();
                (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
              }
            }}
            onDragLeave={(e) => {
              (e.currentTarget as HTMLElement).style.outline = "";
              (e.currentTarget as HTMLElement).style.outlineOffset = "";
            }}
            onDrop={(e) => {
              (e.currentTarget as HTMLElement).style.outline = "";
              (e.currentTarget as HTMLElement).style.outlineOffset = "";
              droppedInZoneRef.current = true;
              const raw = e.dataTransfer.getData("application/x-dimref-delete");
              if (!raw) return;
              e.preventDefault();
              e.stopPropagation();
              try {
                const payload = JSON.parse(raw);
                if (payload.condId !== condId) return;
                onReorderDimrefs?.(payload.dimrefId, dr.id, zoneType, zoneId);
              } catch (err) {
                console.error("Reorder drop error:", err);
              }
            }}
            onDragEnd={(e) => {
              (e.currentTarget as HTMLElement).style.opacity = "1";
              if (!droppedInZoneRef.current) {
                onDeleteDimref(dr.id);
              }
            }}
            style={buttonStyle}
            title="Drag out to remove"
          >
            {dim.name}
          </div>
        );
      })}

      {canRemoveZone && (
        <button
          onClick={onRemoveZone}
          style={{
            marginLeft: "auto",
            border: "none",
            background: "transparent",
            color: mode === "dark" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.3)",
            cursor: "pointer",
            padding: "2px 6px",
            fontSize: "1.2em",
            lineHeight: 1
          }}
          title="Remove Participant"
        >
          ×
        </button>
      )}
    </div>
  );
};

/**
 * Sync Participant Editor with Drag & Drop:
 */
/**
 * FollowConditionEditor:
 * Zeigt IF/THEN-Bereiche an, die jeweils auf eine Metric referenzieren
 * mit Pattern (equal/not_equal/min/max) und Value.
 */
function FollowConditionEditor({ cond }: { cond: ExtendedCondition }) {
  const { metrics = [], updateCondition, mode } = useEditorContext();

  const metricRefIf = cond.metricRefIf || { name: "", patternType: "equal" as const, patternValue: "" };
  const metricRefThen = cond.metricRefThen || { name: "", patternType: "equal" as const, patternValue: "" };

  const renderMetricRefSection = (
    label: string,
    value: { name: string; patternType: "equal" | "not_equal" | "min" | "max"; patternValue: string },
    field: "metricRefIf" | "metricRefThen"
  ) => {
    return (
      <div className="condition-drop-zone">
        <span className="condition-drop-zone__label">
          {label}
        </span>

        {/* Metric Dropdown */}
        <select
          value={value.name}
          onChange={(e) =>
            updateCondition(cond.id, {
              [field]: { ...value, name: e.target.value },
            })
          }
        >
          <option value="">– Metric –</option>
          {metrics.map((m) => (
            <option key={m.id} value={m.name}>
              {m.name}
            </option>
          ))}
        </select>

        {/* Pattern Dropdown */}
        <select
          value={value.patternType}
          onChange={(e) =>
            updateCondition(cond.id, {
              [field]: { ...value, patternType: e.target.value as any },
            })
          }
        >
          <option value="equal">equal</option>
          <option value="not_equal">not_equal</option>
          <option value="min">min</option>
          <option value="max">max</option>
        </select>

        {/* Value Input */}
        <span style={{ position: "relative", display: "inline-block" }}>
          <input
            type="text"
            value={value.patternValue}
            onChange={(e) =>
              updateCondition(cond.id, {
                [field]: { ...value, patternValue: e.target.value.replace(/[^0-9,]/g, "") },
              })
            }
            style={{ maxWidth: "45px", paddingRight: "14px" }}
          />
          <span style={{
            position: "absolute",
            right: 3,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            lineHeight: 0.8,
            fontSize: "1em",
            color: mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
            cursor: "pointer",
            userSelect: "none",
          }}>
            <span
              onClick={() => {
                const val = parseInt(value.patternValue) || 0;
                updateCondition(cond.id, {
                  [field]: { ...value, patternValue: String(val + 1) },
                });
              }}
              style={{ cursor: "pointer" }}
              title="Increase"
            >⌃</span>
            <span
              onClick={() => {
                const val = parseInt(value.patternValue) || 0;
                updateCondition(cond.id, {
                  [field]: { ...value, patternValue: String(Math.max(0, val - 1)) },
                });
              }}
              style={{ cursor: "pointer", transform: "rotate(180deg)" }}
              title="Decrease"
            >⌃</span>
          </span>
        </span>
      </div>
    );
  };

  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      {renderMetricRefSection("If", metricRefIf, "metricRefIf")}
      {renderMetricRefSection("Then", metricRefThen, "metricRefThen")}
    </div>
  );
}

function AlignConditionEditor({ cond }: { cond: ExtendedCondition }) {
  const { dimensions, updateCondition, mode } = useEditorContext();

  const participants: AlignParticipantExt[] = (cond.participants ?? []) as any;
  const onDimrefs = cond.on?.dimrefs ?? [];

  const ensureInit = () => {
    // Ensure at least 2 participants exist
    if (!cond.participants || cond.participants.length < 2) {
      const current = (cond.participants || []) as AlignParticipantExt[];
      const needed = 2 - current.length;
      const newParts = Array.from({ length: needed }).map(() => ({
        id: makeId("participant"),
        dimrefs: [],
      }));
      updateCondition(cond.id, {
        participants: [...current, ...newParts],
        on: cond.on || { dimrefs: [] },
      });
    }
  };

  useEffect(() => {
    if (cond.type === "align") ensureInit();
  }, [cond.type, cond.participants?.length]);

  const addParticipant = () => {
    updateCondition(cond.id, {
      participants: [
        ...participants,
        {
          id: makeId("participant"),
          dimrefs: [],
        },
      ],
    });
  };

  const removeParticipant = (pid: string) => {
    updateCondition(cond.id, {
      participants: participants.filter((p) => p.id !== pid),
    });
  };

  // Helper to update a participant's dimrefs
  const updateParticipantDimrefs = (pid: string, newDimrefs: DimRefExt[]) => {
    updateCondition(cond.id, {
      participants: participants.map((p) => (p.id === pid ? { ...p, dimrefs: newDimrefs } : p)),
    });
  };

  // Track if drag was dropped inside a valid zone (to distinguish delete vs. reorder)
  const droppedInZoneRef = useRef(false);

  const reorderInArray = <T,>(list: T[], fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return list;
    const next = [...list];
    const [moved] = next.splice(fromIndex, 1);
    const insertIndex = toIndex;
    next.splice(insertIndex, 0, moved);
    return next;
  };

  const getZoneDimrefs = (zoneType: "participant" | "on", zoneId?: string) => {
    if (zoneType === "participant") {
      const p = participants.find((part) => part.id === zoneId);
      return p?.dimrefs ?? [];
    }
    return onDimrefs;
  };

  const updateZoneDimrefs = (
    zoneType: "participant" | "on",
    zoneId: string | undefined,
    nextDimrefs: DimRefExt[]
  ) => {
    if (zoneType === "participant") {
      if (!zoneId) return;
      updateCondition(cond.id, {
        participants: participants.map((p) => (p.id === zoneId ? { ...p, dimrefs: nextDimrefs } : p)),
      });
      return;
    }
    if (zoneType === "on") {
      updateCondition(cond.id, { on: { dimrefs: nextDimrefs } });
      return;
    }
  };

  const reorderDimrefsInZone = (
    sourceDimrefId: string,
    targetDimrefId: string,
    zoneType: "participant" | "on",
    zoneId?: string
  ) => {
    const list = getZoneDimrefs(zoneType, zoneId);
    const fromIndex = list.findIndex((d) => d.id === sourceDimrefId);
    const toIndex = list.findIndex((d) => d.id === targetDimrefId);
    if (fromIndex === -1 || toIndex === -1) return;
    const next = reorderInArray(list, fromIndex, toIndex);
    updateZoneDimrefs(zoneType, zoneId, next);
  };

  const reorderPointInZone = (
    dimrefId: string,
    fromPointId: string,
    toPointId: string,
    zoneType: "participant" | "on",
    zoneId?: string
  ) => {
    const list = getZoneDimrefs(zoneType, zoneId);
    const nextDimrefs = list.map((d) => {
      if (d.id !== dimrefId || d.mode !== "points" || !d.pointIds) return d;
      const fromIndex = d.pointIds.indexOf(fromPointId);
      const toIndex = d.pointIds.indexOf(toPointId);
      if (fromIndex === -1 || toIndex === -1) return d;
      return { ...d, pointIds: reorderInArray(d.pointIds, fromIndex, toIndex) };
    });
    updateZoneDimrefs(zoneType, zoneId, nextDimrefs);
  };

  const reorderTagInZone = (
    dimrefId: string,
    fromTagId: string,
    toTagId: string,
    zoneType: "participant" | "on",
    zoneId?: string
  ) => {
    const list = getZoneDimrefs(zoneType, zoneId);
    const nextDimrefs = list.map((d) => {
      if (d.id !== dimrefId || d.mode !== "tags" || !d.tagIds) return d;
      const fromIndex = d.tagIds.indexOf(fromTagId);
      const toIndex = d.tagIds.indexOf(toTagId);
      if (fromIndex === -1 || toIndex === -1) return d;
      return { ...d, tagIds: reorderInArray(d.tagIds, fromIndex, toIndex) };
    });
    updateZoneDimrefs(zoneType, zoneId, nextDimrefs);
  };

  // Generic Drop Handler for Sync Zones
  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetType: "participant" | "on",
    targetId?: string // participant ID
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark as valid drop inside zone (prevents delete in onDragEnd)
    droppedInZoneRef.current = true;

    // Reset styles
    e.currentTarget.style.borderColor = mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
    e.currentTarget.style.background = mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";

    const data = e.dataTransfer.getData("application/x-zeitgeist");
    const internalData = e.dataTransfer.getData("application/x-dimref-delete");

    // Helper to add a new dimref based on payload
    const createDimref = (payload: any): DimRefExt => {
      if (payload.kind === "create-condition-from-dimension-point") {
        return {
          id: makeId("dimref"),
          dimensionId: payload.dimensionId,
          role: "for",
          mode: "points",
          pointIds: [payload.pointId],
          tagIds: [],
        };
      } else if (payload.kind === "create-condition-from-dimension-tag") {
        return {
          id: makeId("dimref"),
          dimensionId: payload.dimensionId,
          role: "for",
          mode: "tags",
          tagIds: [payload.tagId],
          pointIds: [],
        };
      } else {
        return {
          id: makeId("dimref"),
          dimensionId: payload.dimensionId,
          role: "for",
          mode: "all",
          tagIds: [],
          pointIds: [],
        };
      }
    };

    const mergeOrAdd = (list: DimRefExt[], payload: any): DimRefExt[] => {
      // Find if dimension exists (regardless of mode)
      const existingIdx = list.findIndex(d => d.dimensionId === payload.dimensionId);

      if (existingIdx !== -1) {
        // Update existing entry (Replace "All" with Point/Tag, or Merge Points/Tags)
        return list.map((d, i) => {
          if (i !== existingIdx) return d;

          if (payload.kind === "create-condition-from-dimension-point") {
            // If already points mode, merge. Else (was All/Tags), start fresh with this point.
            const currentPoints = d.mode === "points" ? (d.pointIds || []) : [];
            const newPoints = currentPoints.includes(payload.pointId) ? currentPoints : [...currentPoints, payload.pointId];
            return {
              ...d,
              mode: "points",
              pointIds: newPoints,
              tagIds: [] // Clear tags if switching/staying points
            };
          } else if (payload.kind === "create-condition-from-dimension-tag") {
            // If already tags mode, merge. Else (was All/Points), start fresh with this tag.
            const currentTags = d.mode === "tags" ? (d.tagIds || []) : [];
            const newTags = currentTags.includes(payload.tagId) ? currentTags : [...currentTags, payload.tagId];
            return {
              ...d,
              mode: "tags",
              tagIds: newTags,
              pointIds: [] // Clear points if switching/staying tags
            };
          } else if (payload.kind === "create-condition-from-dimension") {
            // Reset to "All" (replace points/tags)
            return {
              ...d,
              mode: "all",
              pointIds: [],
              tagIds: []
            };
          }
          return d;
        });
      }
      // New entry
      return [...list, createDimref(payload)];
    };

    if (internalData) {
      // Internal Move / Delete logic
      try {
        const payload = JSON.parse(internalData);
        if (payload.condId !== cond.id) return; // Only same condition

        // 1. Find Source
        let foundDimRef: DimRefExt | undefined;
        let sourceZone: "participant" | "on" | undefined;
        let sourcePid: string | undefined;

        for (const p of participants) {
          const d = p.dimrefs.find(x => x.id === payload.dimrefId);
          if (d) { foundDimRef = d; sourceZone = "participant"; sourcePid = p.id; break; }
        }
        if (!foundDimRef) {
          foundDimRef = onDimrefs.find(x => x.id === payload.dimrefId);
          if (foundDimRef) sourceZone = "on";
        }

        if (!foundDimRef) return;

        // 2. Construct "Add" payload
        let addPayload: any = null;
        if (payload.type === "point") {
          addPayload = {
            kind: "create-condition-from-dimension-point",
            dimensionId: foundDimRef.dimensionId,
            pointId: payload.pointId,
          };
        } else if (payload.type === "tag") {
          addPayload = {
            kind: "create-condition-from-dimension-tag",
            dimensionId: foundDimRef.dimensionId,
            tagId: payload.tagId,
          };
        } else {
          addPayload = {
            kind: "create-condition-from-dimension",
            dimensionId: foundDimRef.dimensionId,
          };
        }

        // 3. Prepare New State
        if (sourceZone === targetType && sourcePid === targetId) {
          return;
        }

        let newParticipants = [...participants];
        let newOn = [...onDimrefs];

        // Add to Target
        if (targetType === "participant" && targetId) {
          const pIdx = newParticipants.findIndex(p => p.id === targetId);
          if (pIdx !== -1) {
            newParticipants[pIdx] = {
              ...newParticipants[pIdx],
              dimrefs: mergeOrAdd(newParticipants[pIdx].dimrefs, addPayload)
            };
          }
        } else if (targetType === "on") {
          newOn = mergeOrAdd(newOn, addPayload);
        }

        // Remove from Source
        const removeFromList = (list: DimRefExt[]) => {
          const dimref = list.find(d => d.id === payload.dimrefId);
          if (!dimref) return list;

          if (payload.type === "point") {
            const next = dimref.pointIds.filter(id => id !== payload.pointId);
            if (next.length === 0) return list.filter(d => d.id !== payload.dimrefId);
            return list.map(d => d.id === payload.dimrefId ? { ...d, pointIds: next } : d);
          } else if (payload.type === "tag") {
            const next = dimref.tagIds.filter(id => id !== payload.tagId);
            if (next.length === 0) return list.filter(d => d.id !== payload.dimrefId);
            return list.map(d => d.id === payload.dimrefId ? { ...d, tagIds: next } : d);
          } else {
            return list.filter(d => d.id !== payload.dimrefId);
          }
        };

        if (sourceZone === "participant" && sourcePid) {
          const pIdx = newParticipants.findIndex(p => p.id === sourcePid);
          if (pIdx !== -1) {
            newParticipants[pIdx] = {
              ...newParticipants[pIdx],
              dimrefs: removeFromList(newParticipants[pIdx].dimrefs)
            };
          }
        } else if (sourceZone === "on") {
          newOn = removeFromList(newOn);
        }

        updateCondition(cond.id, {
          participants: newParticipants,
          on: { dimrefs: newOn }
        });

      } catch (e) { console.error(e); }
      return;
    }

    if (data) {
      try {
        const payload = JSON.parse(data);
        if (targetType === "participant" && targetId) {
          const p = participants.find(part => part.id === targetId);
          if (p) {
            updateParticipantDimrefs(targetId, mergeOrAdd(p.dimrefs, payload));
          }
        } else if (targetType === "on") {
          updateCondition(cond.id, { on: { dimrefs: mergeOrAdd(onDimrefs, payload) } });
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Participants */}
      {participants.map((p, idx) => (
        <AlignDropZone
          key={p.id}
          title={`Participant ${idx + 1}`}
          dimrefs={p.dimrefs}
          zoneType="participant"
          zoneId={p.id}
          canRemoveZone={participants.length > 2}
          onRemoveZone={() => removeParticipant(p.id)}
          onUpdateDimref={() => { }}
          onReorderDimrefs={reorderDimrefsInZone}
          onReorderPoint={reorderPointInZone}
          onReorderTag={reorderTagInZone}
          onDeleteDimref={(dimrefId, pointId, tagId) => {
            const dimref = p.dimrefs.find(d => d.id === dimrefId);
            if (!dimref) return;

            if (pointId) {
              const next = dimref.pointIds.filter(id => id !== pointId);
              if (next.length === 0) {
                updateParticipantDimrefs(p.id, p.dimrefs.filter(d => d.id !== dimrefId));
              } else {
                updateParticipantDimrefs(p.id, p.dimrefs.map(d => d.id === dimrefId ? { ...d, pointIds: next } : d));
              }
            } else if (tagId) {
              const next = dimref.tagIds.filter(id => id !== tagId);
              if (next.length === 0) {
                updateParticipantDimrefs(p.id, p.dimrefs.filter(d => d.id !== dimrefId));
              } else {
                updateParticipantDimrefs(p.id, p.dimrefs.map(d => d.id === dimrefId ? { ...d, tagIds: next } : d));
              }
            } else {
              // Remove whole dimref
              updateParticipantDimrefs(p.id, p.dimrefs.filter(d => d.id !== dimrefId));
            }
          }}
          handleDrop={handleDrop}
          mode={mode}
          dimensions={dimensions}
          condId={cond.id}
          droppedInZoneRef={droppedInZoneRef}
        />
      ))}

      {/* ... Add Participant Button ... */}
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <button
          onClick={addParticipant}
          style={{
            fontSize: "0.8em",
            padding: "4px 8px",
            borderRadius: 6,
            border: mode === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
            background: "rgba(0,0,0,0.05)",
            cursor: "pointer",
            color: "inherit"
          }}
        >
          + Add Participant
        </button>
      </div>

      <div style={{ height: 1, background: mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", margin: "4px 0" }} />

      {/* On */}
      <AlignDropZone
        title="On"
        dimrefs={onDimrefs}
        zoneType="on"
        onReorderDimrefs={reorderDimrefsInZone}
        onReorderPoint={reorderPointInZone}
        onReorderTag={reorderTagInZone}
        onDeleteDimref={(dimrefId, pointId, tagId) => {
          const list = onDimrefs;
          const dimref = list.find(d => d.id === dimrefId);
          if (!dimref) return;

          let newList = list;
          if (pointId) {
            const next = dimref.pointIds.filter(id => id !== pointId);
            if (next.length === 0) newList = list.filter(d => d.id !== dimrefId);
            else newList = list.map(d => d.id === dimrefId ? { ...d, pointIds: next } : d);
          } else if (tagId) {
            const next = dimref.tagIds.filter(id => id !== tagId);
            if (next.length === 0) newList = list.filter(d => d.id !== dimrefId);
            else newList = list.map(d => d.id === dimrefId ? { ...d, tagIds: next } : d);
          } else {
            newList = list.filter(d => d.id !== dimrefId);
          }
          updateCondition(cond.id, { on: { dimrefs: newList } });
        }}
        onUpdateDimref={() => { }}
        handleDrop={handleDrop}
        mode={mode}
        dimensions={dimensions}
        condId={cond.id}
        droppedInZoneRef={droppedInZoneRef}
      />

    </div>
  );
}
/**
 * ConditionCard:
 * - Anzeige einer einzelnen Condition
 * - Header mit Name (bearbeitbar), Fulfillment-Indikator und Drag-Handle
 * - bei expanded: Detailkonfiguration (Typ, Pattern, DimRefs inkl. DnD)
 * - kann auch im DragOverlay (isOverlay) angezeigt werden
 */
function ConditionCard({
  cond,
  isOverlay,
  style,
  listeners,
  attributes,
}: {
  cond: ExtendedCondition;
  isOverlay?: boolean;
  style?: React.CSSProperties;
  listeners?: any;
  attributes?: any;
}) {
  const {
    dimensions,
    timings,
    userHue,
    mode,
    allNames,
    conditionStatsMap,
    conditionSyntaxMap,
    expandedId,
    setExpandedId,
    updateCondition,
  } = useEditorContext();

  const expanded = expandedId === cond.id;
  const timingNames = useMemo(() => {
    const names = (timings || []).map((timing) => timing.name).filter(Boolean);
    if (cond.time_ref && !names.includes(cond.time_ref)) {
      return [...names, cond.time_ref];
    }
    return names;
  }, [timings, cond.time_ref]);

  // Statistik-Daten zur Condition (werden über den Namen gemappt)
  const stat = conditionStatsMap.get(cond.name);
  const syntaxStat = conditionSyntaxMap.get(cond.name);
  const indicator = getFulfillmentIndicator(stat?.Fulfillment, mode);

  // Prüfen, ob der Name mehrfach vorkommt (Duplikat Hinweis)
  const isDuplicate = allNames.filter((n: string) => n === cond.name).length > 1;

  // interner State: Name gerade im Edit-Modus
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);
  const [isHoveringFulfillment, setIsHoveringFulfillment] = useState(false);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const [pendingRole, setPendingRole] = useState<DimRefRole>("where");

  // DnD-Sensoren für die DimRef-Liste innerhalb der Condition
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Fix: Wenn Maus schnell aus dem Fenster geht, verliert dnd-kit den mouseup-Event.
  // Wir dispatchen einen synthetischen mouseup, damit dnd-kit den Drag sauber beendet.
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.relatedTarget === null) {
        document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
      }
    };
    window.addEventListener('mouseleave', handleMouseLeave);
    return () => window.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  // Backend syntax check
  const backendErrors = syntaxStat?.SyntaxMessages;
  const hasBackendError = backendErrors && backendErrors.length > 0;

  // Nur noch Backend-Fehler berücksichtigen
  const errorMessage = hasBackendError
    ? (Array.isArray(backendErrors) ? backendErrors.map(e => String(e)).join(", ") : String(backendErrors))
    : null;

  const isValid = !errorMessage;

  // Symbol je nach Fehler-Typ (sehr einfach: wenn "warning" im Text vorkommt -> ❗️, sonst ⚠️)
  const validationIcon = !isValid && errorMessage && /warning/i.test(errorMessage) ? "❗️" : "⚠️";

  const reorderInArray = <T,>(list: T[], fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return list;
    const next = [...list];
    const [moved] = next.splice(fromIndex, 1);
    const insertIndex = toIndex;
    next.splice(insertIndex, 0, moved);
    return next;
  };

  const reorderRoleDimrefs = (sourceDimrefId: string, targetDimrefId: string, role: DimRefRole) => {
    const sourceIdx = cond.dimrefs.findIndex((d) => d.id === sourceDimrefId);
    const targetIdx = cond.dimrefs.findIndex((d) => d.id === targetDimrefId);
    if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) return;
    const source = cond.dimrefs[sourceIdx];
    const target = cond.dimrefs[targetIdx];
    if (source.role !== role || target.role !== role) return;

    const next = reorderInArray(cond.dimrefs, sourceIdx, targetIdx);
    updateCondition(cond.id, { dimrefs: next });
  };

  const reorderPointIds = (dimrefId: string, fromPointId: string, toPointId: string) => {
    const dimref = cond.dimrefs.find((d) => d.id === dimrefId);
    if (!dimref || !dimref.pointIds || dimref.mode !== "points") return;
    const fromIndex = dimref.pointIds.indexOf(fromPointId);
    const toIndex = dimref.pointIds.indexOf(toPointId);
    if (fromIndex === -1 || toIndex === -1) return;
    const nextPoints = reorderInArray(dimref.pointIds, fromIndex, toIndex);
    updateCondition(cond.id, {
      dimrefs: cond.dimrefs.map((d) => (d.id === dimrefId ? { ...d, pointIds: nextPoints } : d)),
    });
  };

  const reorderTagIds = (dimrefId: string, fromTagId: string, toTagId: string) => {
    const dimref = cond.dimrefs.find((d) => d.id === dimrefId);
    if (!dimref || !dimref.tagIds || dimref.mode !== "tags") return;
    const fromIndex = dimref.tagIds.indexOf(fromTagId);
    const toIndex = dimref.tagIds.indexOf(toTagId);
    if (fromIndex === -1 || toIndex === -1) return;
    const nextTags = reorderInArray(dimref.tagIds, fromIndex, toIndex);
    updateCondition(cond.id, {
      dimrefs: cond.dimrefs.map((d) => (d.id === dimrefId ? { ...d, tagIds: nextTags } : d)),
    });
  };

  return (
    <div
      ref={cardRef}
      style={{
        ...style,
        position: style?.position,
        background: getConditionColor(cond, stat, syntaxStat, userHue, mode),
        margin: "8px 0",
        padding: 8,
        borderRadius: 6,
        color: "inherit",
        opacity: isOverlay ? 0.8 : 1,
        boxShadow: cond.enforcement === "hard"
          ? "inset 0 0 0 2px rgba(0,0,0,0.15)" // Harter innerer Rand
          : isOverlay ? "0 4px 12px rgba(0,0,0,0.2)" : undefined,
      }}
      onDragOver={(e) => {
        if (!expanded) return;
        // Allow adding more criteria by dragging points
        if (e.dataTransfer.types.includes("application/x-zeitgeist")) {
          e.preventDefault();
        }
      }}
      onDrop={(e) => {
        if (!expanded) return;
        // Sync-Conditions handle Drops internally (in AlignConditionEditor)
        if (cond.type === "align") return;
        // Follow conditions also don't use the generic drop (they have metric_ref, not dimref pattern)
        // but they DO use the standard FOR/WHERE drop zones below, so we don't return here.

        // Catch drop to add criteria (dimref)
        const data = e.dataTransfer.getData("application/x-zeitgeist");
        // Removed the default "add filter/each" drop logic.
        // Drops must now occur specifically on the nested Role DropZones (FOCUS, PER, FILTER, EACH)
      }}
      {...attributes}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {/* Drag Handle für die ganze Condition-Karte */}
        <div {...listeners} style={{ cursor: "grab" }} title="Drag to reorder · Drag out to remove">☰</div>

        {/* Pfeil zum Auf-/Zuklappen (nicht im Overlay klickbar) */}
        <span
          style={{ cursor: "pointer", padding: "0 6px", userSelect: "none" }}
          onClick={() => !isOverlay && setExpandedId(expanded ? null : cond.id)}
        >
          {expanded ? "▾" : "▸"}
        </span>

        {/* Name der Condition: Doppelklick startet Editieren */}
        {isEditingName ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <input
              type="text"
              value={cond.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS
                    .filter(c => raw.includes(c))
                    .map(c => c === "\n" ? "\u21b5" : c)
                    .join(", ");
                  setSpecialCharWarning(found);
                  setTimeout(() => setSpecialCharWarning(null), 2000);
                }
                updateCondition(cond.id, { name: stripped });
              }}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter") setIsEditingName(false); }}
              autoFocus
              onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
              style={{ border: isDuplicate ? "1px solid #d66" : undefined }}
            />
            {specialCharWarning && (
              <span style={{ color: "#e55", fontSize: "0.75em" }}>
                "{specialCharWarning}" nicht erlaubt
              </span>
            )}
          </div>
        ) : (
          <div
            style={{ flex: 1, userSelect: "none", display: "flex", alignItems: "center", gap: 8 }}
            onDoubleClick={() => !isOverlay && setIsEditingName(true)}
          >
            <span>{cond.name}</span>

            {/* Validation Warning */}
            {!isValid && !isOverlay && (
              <span
                title={errorMessage || ""}
                style={{
                  fontSize: "1.2em",
                  cursor: "help",
                  animation: "pulse 2s infinite"
                }}
              >
                {validationIcon}
              </span>
            )}
          </div>
        )}

          {/* Fulfillment-Indikator immer ganz rechts */}
          {indicator && (
            <span
              ref={indicatorRef}
              style={{ fontSize: "0.8em", fontWeight: "bold", color: indicator.color, cursor: "default", display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2, flexShrink: 0, marginLeft: "auto" }}
              onMouseEnter={() => {
                if (stat?.violations?.length > 0) {
                  setIsHoveringFulfillment(true);
                }
              }}
              onMouseLeave={() => setIsHoveringFulfillment(false)}
            >
              <span>{indicator.emoji}</span>
              <span>{indicator.pct}</span>
              {/* Violations Tooltip via Portal */}
              {isHoveringFulfillment && stat?.violations && stat.violations.length > 0 && indicatorRef.current && createPortal(
                  <div
                    style={{
                      position: "fixed",
                      top: indicatorRef.current.getBoundingClientRect().bottom + 8,
                      // Anchor to the right edge of the CARD (container) to keep it inside admin bounds cleanly
                      // Fallback to indicator if cardRef is missing (should not happen)
                      right: cardRef.current
                        ? document.body.clientWidth - cardRef.current.getBoundingClientRect().right
                        : document.body.clientWidth - indicatorRef.current.getBoundingClientRect().right,

                      // Constrain width to the card width (minus some padding)
                      maxWidth: cardRef.current
                        ? cardRef.current.getBoundingClientRect().width - 10
                        : 300,
                      minWidth: 200,

                      // Style matching .cell-dropdown-list
                      background: "linear-gradient(to bottom, var(--pane-bg) 0%, color-mix(in srgb, var(--pane-bg) 95%, black) 100%)",
                      backdropFilter: "blur(12px)",
                      WebkitBackdropFilter: "blur(12px)",
                      border: "1px solid color-mix(in srgb, var(--pane-border) 60%, transparent)",
                      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.15)",
                      borderRadius: 10,
                      zIndex: 9999,
                      padding: "4px 0",
                      color: "var(--text-color)",
                      textAlign: "left",
                      pointerEvents: "none",
                    }}
                  >
                    <div style={{
                      fontWeight: 600,
                      padding: "8px 14px 4px",
                      fontSize: "0.95em",
                      opacity: 0.9,
                      borderBottom: "1px solid rgba(128,128,128,0.1)"
                    }}>
                      {stat.violations.length} Violation{stat.violations.length !== 1 ? "s" : ""}
                    </div>
                    <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                      {stat.violations.map((v: any, i: number) => {
                        const filterText = v.where
                          ? Object.values(v.where).join(", ")
                          : "Global";
                        return (
                          <div
                            key={i}
                            className="cell-dropdown-item"
                            style={{
                              margin: "2px 6px",
                              cursor: "default",
                              fontSize: "0.9em",
                              animationDelay: `${i * 0.05}s`,
                              pointerEvents: "auto"
                            }}
                          >
                            {filterText}
                          </div>
                        );
                      })}
                    </div>
                  </div>,
                  document.body
                )}
              </span>
            )}
      </div>

      {/* Duplikat-Hinweis unter dem Header */}
      {isDuplicate && (
        <div style={{ color: "#d66", fontSize: "0.8em", marginTop: 4 }}>Name already in use</div>
      )}

      {/* Detailbereich nur anzeigen, wenn expanded und nicht im Overlay */}
      {expanded && !isOverlay && (
        <div style={{ marginTop: 8 }}>
          {/* Condition-Typ Auswahl */}
          <div style={{ marginTop: 6, display: "flex", gap: "1em", flexWrap: "wrap" }}>
            {/* Enforcement zuerst */}
            <label>
              Enforcement:{" "}
              <select
                value={cond.enforcement || "soft"}
                onChange={(e) => updateCondition(cond.id, { enforcement: e.target.value as any })}
              >
                <option value="soft">soft</option>
                <option value="hard">hard</option>
                <option value="off">off</option>
              </select>
            </label>

            {/* Scope */}
            <label>
              Scope:{" "}
              <select
                value={cond.scope || "always"}
                onChange={(e) => updateCondition(cond.id, { scope: e.target.value as any })}
              >
                <option value="always">always</option>
                <option value="template">template</option>
                <option value="period">period</option>
                <option value="day">day</option>
                <option value="repair">repair</option>
              </select>
            </label>

            {/* Mode – nur wenn scope nicht "always" */}
            {(cond.scope && cond.scope !== "always") && (
              <label>
                Mode:{" "}
                <select
                  value={cond.mode || "any"}
                  onChange={(e) => updateCondition(cond.id, { mode: e.target.value as any })}
                >
                  <option value="any">any</option>
                  <option value="shortterm">shortterm</option>
                  <option value="longterm">longterm</option>
                </select>
              </label>
            )}

            {/* Condition-Typ Auswahl */}
            <label>
              Type:{" "}
              <select
                value={cond.type}
                onChange={(e) => {
                  const nextType = e.target.value as any;
                  const prevType = cond.type;

                  const isAlign = (t: string) => t === "align";
                  const isFollow = (t: string) => t === "follow";
                  const isGroupB = (t: string) =>
                    t === "count" || t === "distinct" || t === "gap" || t === "block" || t === "time.sum" || t === "time.gap" || t === "follow";

                  // Wenn zwischen sync <-> (count|block|distinct) gewechselt wird, sollen die
                  // jeweiligen typen-spezifischen Felder zurückgesetzt werden.
                  // - Wechsel zu sync: patternValue leeren (damit er nicht "hängen bleibt")
                  // - Wechsel weg von sync: align-spezifische Struktur leeren
                  const groupChanged =
                    (isAlign(prevType) && isGroupB(nextType)) || (isGroupB(prevType) && isAlign(nextType))
                    || (isFollow(prevType) && !isFollow(nextType)) || (!isFollow(prevType) && isFollow(nextType));

                  if (groupChanged) {
                    if (isAlign(nextType)) {
                      updateCondition(cond.id, {
                        type: nextType,
                        patternValue: "",
                        metricRefIf: undefined,
                        metricRefThen: undefined,
                      });
                    } else if (isFollow(nextType)) {
                      updateCondition(cond.id, {
                        type: nextType,
                        participants: undefined,
                        on: undefined,
                        metricRefIf: { name: "", patternType: "equal", patternValue: "" },
                        metricRefThen: { name: "", patternType: "equal", patternValue: "" },
                      });
                    } else {
                      let nextDimrefs = cond.dimrefs.filter((d) => (d.role as string) !== "each");
                      if (nextType === "count" || nextType === "time.sum" || nextType === "time.gap") {
                        nextDimrefs = nextDimrefs.filter((d) => d.role !== "on");
                      }
                      updateCondition(cond.id, {
                        type: nextType,
                        participants: undefined,
                        on: undefined,
                        metricRefIf: undefined,
                        metricRefThen: undefined,
                        dimrefs: nextDimrefs,
                      });
                    }
                    return;
                  }

                  let updates: any = { type: nextType };
                  let nextDimrefs = cond.dimrefs.filter((d) => (d.role as string) !== "each");
                  if (nextType === "count" || nextType === "time.sum" || nextType === "time.gap" || nextType === "follow") {
                    nextDimrefs = nextDimrefs.filter((d) => d.role !== "on");
                  }
                  if (nextDimrefs.length !== cond.dimrefs.length) {
                    updates.dimrefs = nextDimrefs;
                  }
                  updateCondition(cond.id, updates);
                }}
              >
                <option value="count">count</option>
                <option value="distinct">distinct</option>
                <option value="gap">gap</option>
                <option value="block">block</option>
                <option value="align">align</option>
                <option value="follow">follow</option>
                <option value="time.sum">time.sum</option>
                <option value="time.gap">time.gap</option>
              </select>
            </label>

            {cond.type !== "follow" && (
            <label>
              Timing:{" "}
              <select
                value={cond.time_ref || ""}
                onChange={(e) =>
                  updateCondition(cond.id, {
                    time_ref: e.target.value || undefined,
                  })
                }
              >
                <option value="">-</option>
                {timingNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            )}
          </div>

          {/* Pattern-Typ + Eingabefeld für patternValue (nur Zahlen und Kommas) */}
          {cond.type !== "follow" && (

            <div style={{ marginTop: 6 }}>
              <label>
                Pattern:{" "}
                <select
                  value={cond.patternType}
                  onChange={(e) => updateCondition(cond.id, { patternType: e.target.value as any })}
                >
                  <option value="equal">equal</option>
                  <option value="not_equal">not_equal</option>
                  <option value="min">min</option>
                  <option value="max">max</option>
                </select>
              </label>
              <span style={{ position: "relative", display: "inline-block", marginLeft: 6 }}>
                <input
                  type="text"
                  value={cond.patternValue}
                  onChange={(e) =>
                    updateCondition(cond.id, {
                      patternValue: e.target.value.replace(/[^0-9,]/g, ""),
                    })
                  }
                  style={{ maxWidth: "45px", paddingRight: "14px" }}
                />
                <span style={{
                  position: "absolute",
                  right: 3,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  lineHeight: 0.8,
                  fontSize: "1em",
                  color: mode === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
                  cursor: "pointer",
                  userSelect: "none",
                }}>
                  <span
                    onClick={() => {
                      const val = parseInt(cond.patternValue) || 0;
                      updateCondition(cond.id, { patternValue: String(val + 1) });
                    }}
                    style={{ cursor: "pointer" }}
                    title="Increase"
                  >⌃</span>
                  <span
                    onClick={() => {
                      const val = parseInt(cond.patternValue) || 0;
                      updateCondition(cond.id, { patternValue: String(Math.max(0, val - 1)) });
                    }}
                    style={{ cursor: "pointer", transform: "rotate(180deg)" }}
                    title="Decrease"
                  >⌃</span>
                </span>
              </span>
            </div>
          )}

          {/* Follow Condition Editor (IF/THEN metric_ref) */}
          {cond.type === "follow" && <FollowConditionEditor cond={cond} />}

          {/* Role-Based DimRef Zones */}
          {cond.type !== "align" && !isOverlay && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {(["for", "on", "where"] as DimRefRole[])
                .filter(role => {
                  if ((cond.type === "count" || cond.type === "time.sum" || cond.type === "time.gap" || cond.type === "follow") && role === "on") return false;
                  return true;
                })
                .map((role) => {
                // Get all dimrefs for this role
                const roleDimrefs = cond.dimrefs.filter((dr) => dr.role === role);

                return (
                  <div
                    key={role}
                    data-role={role}
                    className="condition-drop-zone"
                    onDragOver={(e) => {
                      // Accept both external drops (from sidebar) and internal drops (between zones)
                      if (e.dataTransfer.types.includes("application/x-zeitgeist") ||
                        e.dataTransfer.types.includes("application/x-dimref-delete")) {
                        e.preventDefault();
                        e.currentTarget.classList.add("condition-drop-zone--over");
                      }
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove("condition-drop-zone--over");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Reset styles
                      e.currentTarget.classList.remove("condition-drop-zone--over");

                      // First check for internal drop (button moved between zones)
                      const internalData = e.dataTransfer.getData("application/x-dimref-delete");
                      if (internalData) {
                        try {
                          const payload = JSON.parse(internalData);
                          // Only process if same condition
                          if (payload.condId === cond.id) {
                            const dimref = cond.dimrefs.find((d) => d.id === payload.dimrefId);
                            if (dimref && dimref.role !== role) {
                              // Move to new role
                              if (payload.type === "point" && payload.pointId) {
                                // Move single point to new role zone
                                const dim = dimensions.find((d) => d.id === dimref.dimensionId);
                                if (dim) {
                                  // Check if target role already has this dimension
                                  const existingInTarget = cond.dimrefs.find(
                                    (d) => d.dimensionId === dimref.dimensionId && d.role === role
                                  );
                                  if (existingInTarget) {
                                    // Add point to existing dimref in target role
                                    const nextPoints = existingInTarget.pointIds?.includes(payload.pointId)
                                      ? existingInTarget.pointIds
                                      : [...(existingInTarget.pointIds || []), payload.pointId];
                                    // Remove from source, add to target
                                    const sourcePoints = dimref.pointIds?.filter((id) => id !== payload.pointId) || [];
                                    updateCondition(cond.id, {
                                      dimrefs: cond.dimrefs
                                        .map((d) => {
                                          if (d.id === existingInTarget.id) {
                                            return { ...d, mode: "points" as const, pointIds: nextPoints };
                                          }
                                          if (d.id === dimref.id) {
                                            return sourcePoints.length > 0
                                              ? { ...d, pointIds: sourcePoints }
                                              : null; // mark for removal
                                          }
                                          return d;
                                        })
                                        .filter(Boolean) as typeof cond.dimrefs,
                                    });
                                  } else {
                                    // Create new dimref in target role with this point
                                    const sourcePoints = dimref.pointIds?.filter((id) => id !== payload.pointId) || [];
                                    const newDimref = {
                                      id: makeId("dimref"),
                                      dimensionId: dimref.dimensionId,
                                      role,
                                      mode: "points" as const,
                                      pointIds: [payload.pointId],
                                      tagIds: [],
                                    };
                                    updateCondition(cond.id, {
                                      dimrefs: [
                                        ...cond.dimrefs
                                          .map((d) => {
                                            if (d.id === dimref.id) {
                                              return sourcePoints.length > 0 ? { ...d, pointIds: sourcePoints } : null;
                                            }
                                            return d;
                                          })
                                          .filter(Boolean) as typeof cond.dimrefs,
                                        newDimref,
                                      ],
                                    });
                                  }
                                }
                              } else if (payload.type === "tag" && payload.tagId) {
                                // Move single tag to new role zone
                                const existingInTarget = cond.dimrefs.find(
                                  (d) => d.dimensionId === dimref.dimensionId && d.role === role
                                );
                                if (existingInTarget) {
                                  const nextTags = existingInTarget.tagIds?.includes(payload.tagId)
                                    ? existingInTarget.tagIds
                                    : [...(existingInTarget.tagIds || []), payload.tagId];
                                  const sourceTags = dimref.tagIds?.filter((id) => id !== payload.tagId) || [];
                                  updateCondition(cond.id, {
                                    dimrefs: cond.dimrefs
                                      .map((d) => {
                                        if (d.id === existingInTarget.id) {
                                          return { ...d, mode: "tags" as const, tagIds: nextTags };
                                        }
                                        if (d.id === dimref.id) {
                                          return sourceTags.length > 0 ? { ...d, tagIds: sourceTags } : null;
                                        }
                                        return d;
                                      })
                                      .filter(Boolean) as typeof cond.dimrefs,
                                  });
                                } else {
                                  const sourceTags = dimref.tagIds?.filter((id) => id !== payload.tagId) || [];
                                  const newDimref = {
                                    id: makeId("dimref"),
                                    dimensionId: dimref.dimensionId,
                                    role,
                                    mode: "tags" as const,
                                    tagIds: [payload.tagId],
                                    pointIds: [],
                                  };
                                  updateCondition(cond.id, {
                                    dimrefs: [
                                      ...cond.dimrefs
                                        .map((d) => {
                                          if (d.id === dimref.id) {
                                            return sourceTags.length > 0 ? { ...d, tagIds: sourceTags } : null;
                                          }
                                          return d;
                                        })
                                        .filter(Boolean) as typeof cond.dimrefs,
                                      newDimref,
                                    ],
                                  });
                                }
                              } else if (payload.type === "dimension") {
                                // Move entire dimension to new role
                                updateCondition(cond.id, {
                                  dimrefs: cond.dimrefs.map((d) =>
                                    d.id === dimref.id ? { ...d, role } : d
                                  ),
                                });
                              }
                            }
                          }
                        } catch (err) {
                          console.error("Internal drop error:", err);
                        }
                        return; // Don't process as external drop
                      }

                      // External drop (from sidebar): point/tag/dimension
                      const extData = e.dataTransfer.getData("application/x-zeitgeist");
                      if (!extData) return;
                      try {
                        const payload = JSON.parse(extData);

                        if (
                          payload.kind !== "create-condition-from-dimension-point" &&
                          payload.kind !== "create-condition-from-dimension-tag" &&
                          payload.kind !== "create-condition-from-dimension"
                        ) {
                          return;
                        }

                        const existingIdx = cond.dimrefs.findIndex(
                          (d) => d.dimensionId === payload.dimensionId && d.role === role
                        );

                        const applyUpdate = (updated: any) =>
                          updateCondition(cond.id, {
                            dimrefs: cond.dimrefs.map((d, i) => (i === existingIdx ? updated : d)),
                          });

                        if (existingIdx !== -1) {
                          const existing = cond.dimrefs[existingIdx];

                          if (payload.kind === "create-condition-from-dimension") {
                            // Always replace to ALL
                            applyUpdate({
                              ...existing,
                              mode: "all" as const,
                              pointIds: [] as string[],
                              tagIds: [] as string[],
                            });
                            return;
                          }

                          if (payload.kind === "create-condition-from-dimension-point") {
                            // Merge points only if we're already in points-mode; otherwise replace.
                            const nextPoints =
                              existing.mode === "points"
                                ? (existing.pointIds || []).includes(payload.pointId)
                                  ? (existing.pointIds || [])
                                  : [...(existing.pointIds || []), payload.pointId]
                                : [payload.pointId];
                            applyUpdate({
                              ...existing,
                              mode: "points" as const,
                              pointIds: nextPoints,
                              tagIds: [] as string[],
                            });
                            return;
                          }

                          // payload.kind === "create-condition-from-dimension-tag"
                          // Merge tags only if we're already in tags-mode; otherwise replace.
                          const nextTags =
                            existing.mode === "tags"
                              ? (existing.tagIds || []).includes(payload.tagId)
                                ? (existing.tagIds || [])
                                : [...(existing.tagIds || []), payload.tagId]
                              : [payload.tagId];
                          applyUpdate({
                            ...existing,
                            mode: "tags" as const,
                            tagIds: nextTags,
                            pointIds: [] as string[],
                          });
                          return;
                        }

                        // No existing dimref in this role -> create new
                        updateCondition(cond.id, {
                          dimrefs: [
                            ...cond.dimrefs,
                            {
                              id: makeId("dimref"),
                              dimensionId: payload.dimensionId,
                              role,
                              mode:
                                payload.kind === "create-condition-from-dimension-point"
                                  ? ("points" as const)
                                  : payload.kind === "create-condition-from-dimension-tag"
                                    ? ("tags" as const)
                                    : ("all" as const),
                              pointIds:
                                payload.kind === "create-condition-from-dimension-point"
                                  ? ([payload.pointId] as string[])
                                  : ([] as string[]),
                              tagIds:
                                payload.kind === "create-condition-from-dimension-tag"
                                  ? ([payload.tagId] as string[])
                                  : ([] as string[]),
                            },
                          ],
                        });
                      } catch (err) {
                        console.error("Drop parse error:", err);
                      }
                    }}
                  >
                    {/* Role Label */}
                    <span className="condition-drop-zone__label">
                      {role}
                    </span>

                    {/* Dimension Buttons - draggable, drag out to delete */}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
                      {roleDimrefs.flatMap((dr) => {
                        const dim = dimensions.find((d) => d.id === dr.dimensionId);
                        if (!dim) return [];

                        const buttonStyle: React.CSSProperties = {
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 10px",
                          background: mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.06)",
                          borderRadius: 14,
                          fontSize: "14px",
                          fontWeight: 500,
                          color: mode === "dark" ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)",
                          cursor: "grab",
                          userSelect: "none",
                          transition: "all 0.15s ease",
                        };

                        // Points mode: one button per point
                        if (dr.mode === "points" && dr.pointIds?.length) {
                          return dr.pointIds.map((pid) => {
                            const pt = dim.points.find((p: any) => p.id === pid);
                            if (!pt) return null;
                            return (
                              <div
                                key={`${dr.id}-${pid}`}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData("application/x-dimref-delete", JSON.stringify({
                                    type: "point",
                                    dimrefId: dr.id,
                                    pointId: pid,
                                    condId: cond.id,
                                  }));
                                  e.dataTransfer.effectAllowed = "move";
                                  (e.currentTarget as HTMLElement).style.opacity = "0.5";
                                }}
                                onDragOver={(e) => {
                                  if (e.dataTransfer.types.includes("application/x-dimref-delete")) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                                    (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                                  }
                                }}
                                onDragLeave={(e) => {
                                  (e.currentTarget as HTMLElement).style.outline = "";
                                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                                }}
                                onDrop={(e) => {
                                  (e.currentTarget as HTMLElement).style.outline = "";
                                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                                  const raw = e.dataTransfer.getData("application/x-dimref-delete");
                                  if (!raw) return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  try {
                                    const payload = JSON.parse(raw);
                                    if (payload.condId !== cond.id) return;
                                    if (payload.type === "point" && payload.pointId) {
                                      if (payload.dimrefId === dr.id) {
                                        reorderPointIds(dr.id, payload.pointId, pid);
                                      } else {
                                        reorderRoleDimrefs(payload.dimrefId, dr.id, role);
                                      }
                                      return;
                                    }
                                    if (payload.type === "dimension") {
                                      reorderRoleDimrefs(payload.dimrefId, dr.id, role);
                                    }
                                  } catch (err) {
                                    console.error("Reorder drop error:", err);
                                  }
                                }}
                                onDragEnd={(e) => {
                                  (e.currentTarget as HTMLElement).style.opacity = "1";
                                  // Check if dropped outside - dropEffect will be "none"
                                  if (e.dataTransfer.dropEffect === "none") {
                                    const newPointIds = dr.pointIds!.filter((id) => id !== pid);
                                    if (newPointIds.length === 0) {
                                      updateCondition(cond.id, {
                                        dimrefs: cond.dimrefs.filter((d) => d.id !== dr.id),
                                      });
                                    } else {
                                      updateCondition(cond.id, {
                                        dimrefs: cond.dimrefs.map((d) =>
                                          d.id === dr.id ? { ...d, pointIds: newPointIds } : d
                                        ),
                                      });
                                    }
                                  }
                                }}
                                style={buttonStyle}
                                title="Drag out to remove"
                              >
                                {pt.name}
                              </div>
                            );
                          }).filter(Boolean);
                        }

                        // Tags mode: one button per tag
                        if (dr.mode === "tags" && dr.tagIds?.length) {
                          return dr.tagIds.map((tid) => {
                            const tag = dim.tags.find((t: any) => t.id === tid);
                            if (!tag) return null;
                            return (
                              <div
                                key={`${dr.id}-${tid}`}
                                draggable
                                onDragStart={(e) => {
                                  e.dataTransfer.setData("application/x-dimref-delete", JSON.stringify({
                                    type: "tag",
                                    dimrefId: dr.id,
                                    tagId: tid,
                                    condId: cond.id,
                                  }));
                                  e.dataTransfer.effectAllowed = "move";
                                  (e.currentTarget as HTMLElement).style.opacity = "0.5";
                                }}
                                onDragOver={(e) => {
                                  if (e.dataTransfer.types.includes("application/x-dimref-delete")) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                                    (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                                  }
                                }}
                                onDragLeave={(e) => {
                                  (e.currentTarget as HTMLElement).style.outline = "";
                                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                                }}
                                onDrop={(e) => {
                                  (e.currentTarget as HTMLElement).style.outline = "";
                                  (e.currentTarget as HTMLElement).style.outlineOffset = "";
                                  const raw = e.dataTransfer.getData("application/x-dimref-delete");
                                  if (!raw) return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  try {
                                    const payload = JSON.parse(raw);
                                    if (payload.condId !== cond.id) return;
                                    if (payload.type === "tag" && payload.tagId) {
                                      if (payload.dimrefId === dr.id) {
                                        reorderTagIds(dr.id, payload.tagId, tid);
                                      } else {
                                        reorderRoleDimrefs(payload.dimrefId, dr.id, role);
                                      }
                                      return;
                                    }
                                    if (payload.type === "dimension") {
                                      reorderRoleDimrefs(payload.dimrefId, dr.id, role);
                                    }
                                  } catch (err) {
                                    console.error("Reorder drop error:", err);
                                  }
                                }}
                                onDragEnd={(e) => {
                                  (e.currentTarget as HTMLElement).style.opacity = "1";
                                  if (e.dataTransfer.dropEffect === "none") {
                                    const newTagIds = dr.tagIds!.filter((id) => id !== tid);
                                    if (newTagIds.length === 0) {
                                      updateCondition(cond.id, {
                                        dimrefs: cond.dimrefs.filter((d) => d.id !== dr.id),
                                      });
                                    } else {
                                      updateCondition(cond.id, {
                                        dimrefs: cond.dimrefs.map((d) =>
                                          d.id === dr.id ? { ...d, tagIds: newTagIds } : d
                                        ),
                                      });
                                    }
                                  }
                                }}
                                style={buttonStyle}
                                title="Drag out to remove"
                              >
                                {tag.name}
                              </div>
                            );
                          }).filter(Boolean);
                        }

                        // All mode: one button for dimension
                        return [(
                          <div
                            key={dr.id}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("application/x-dimref-delete", JSON.stringify({
                                type: "dimension",
                                dimrefId: dr.id,
                                condId: cond.id,
                              }));
                              e.dataTransfer.effectAllowed = "move";
                              (e.currentTarget as HTMLElement).style.opacity = "0.5";
                            }}
                            onDragOver={(e) => {
                              if (e.dataTransfer.types.includes("application/x-dimref-delete")) {
                                e.preventDefault();
                                e.stopPropagation();
                                (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                                (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                              }
                            }}
                            onDragLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.outline = "";
                              (e.currentTarget as HTMLElement).style.outlineOffset = "";
                            }}
                            onDrop={(e) => {
                              (e.currentTarget as HTMLElement).style.outline = "";
                              (e.currentTarget as HTMLElement).style.outlineOffset = "";
                              const raw = e.dataTransfer.getData("application/x-dimref-delete");
                              if (!raw) return;
                              e.preventDefault();
                              e.stopPropagation();
                              try {
                                const payload = JSON.parse(raw);
                                if (payload.condId !== cond.id) return;
                                if (payload.type === "dimension") {
                                  reorderRoleDimrefs(payload.dimrefId, dr.id, role);
                                } else if (payload.type === "point" || payload.type === "tag") {
                                  reorderRoleDimrefs(payload.dimrefId, dr.id, role);
                                }
                              } catch (err) {
                                console.error("Reorder drop error:", err);
                              }
                            }}
                            onDragEnd={(e) => {
                              (e.currentTarget as HTMLElement).style.opacity = "1";
                              if (e.dataTransfer.dropEffect === "none") {
                                updateCondition(cond.id, {
                                  dimrefs: cond.dimrefs.filter((d) => d.id !== dr.id),
                                });
                              }
                            }}
                            style={buttonStyle}
                            title="Drag out to remove"
                          >
                            {dim.name}
                          </div>
                        )];
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Validation Error Message */}
          {!isValid && !isOverlay && errorMessage && (
            <div className="syntax-error-banner">
              {errorMessage}
            </div>
          )}

          {/* Sync Condition Editor */}
          {cond.type === "align" && <AlignConditionEditor cond={cond} />}
        </div >
      )
      }
    </div >
  );
}

/**
 * SortableConditionCard:
 * - Wrapper um ConditionCard, der sie über useSortable draggen/sortieren lässt.
 */
function SortableConditionCard({ cond, key }: { cond: ExtendedCondition; key?: string | number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cond.id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
      }}
    >
      <ConditionCard cond={cond} listeners={listeners} attributes={attributes} />
    </div>
  );
}

/**
 * GroupCard:
 * - Visuelle Darstellung einer Condition-Gruppe.
 * - Enthält:
 *   - Group-Namen (bearbeitbar)
 *   - Fulfillment-Indikator als Durchschnitt über alle Conditions in der Gruppe
 *   - Collapse/Expand, Drag-Handle (+ Drag-Out-Delete für leere Gruppen), +Condition
 *   - Wrapper für die Condition-Liste (children)
 * - wird auch im DragOverlay verwendet (isOverlay)
 */
function GroupCard({
  group,
  groupConds,
  isOverlay,
  children,
  style,
  listeners,
  attributes,
}: {
  group: ConditionGroup;
  groupConds: ExtendedCondition[];
  isOverlay?: boolean;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  listeners?: any;
  attributes?: any;
}) {
  const {
    userHue,
    mode,
    allNames,
    conditionStatsMap,
    editingGroupId,
    setEditingGroupId,
    editingGroupName,
    setEditingGroupName,
    updateGroup,
    addCondition,
    collapsedGroups,
    toggleGroupCollapse,
    hoverGroupId,
    groups,
    conditions,
    onChange,
    setExpandedId,
  } = useEditorContext();


  const isEditing = editingGroupId === group.id;
  const isCollapsed = collapsedGroups.has(group.id);
  const isDropTarget = hoverGroupId === group.id;
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);

  // Fulfillment-Durchschnitt über alle Conditions der Gruppe (außer enforcement "off")
  const fulfillmentValues = groupConds
    .filter((c) => c.enforcement !== "off") // Conditions mit enforcement "off" ignorieren
    .map((c) => conditionStatsMap.get(c.name)?.Fulfillment)
    .filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
  const groupFulfillment =
    fulfillmentValues.length > 0
      ? fulfillmentValues.reduce((a, b) => a + b, 0) / fulfillmentValues.length
      : null;
  const indicator = getFulfillmentIndicator(groupFulfillment, mode);

  // Hintergrund der Gruppe abhängig von Fulfillment oder Basisfarbe
  const background =
    groupFulfillment != null
      ? colorFromFulfillment(groupFulfillment, mode)
      : getBaseColor(1, "section", userHue, mode);

  // Gemeinsames Enforcement der Gruppe bestimmen
  // Wenn alle Conditions dasselbe Enforcement haben, wird dieser Wert angezeigt
  // Sonst wird "" (Platzhalter) angezeigt
  const getGroupEnforcement = (): string => {
    if (groupConds.length === 0) return "";
    const firstEnforcement = groupConds[0].enforcement || "soft";
    const allSame = groupConds.every((c) => (c.enforcement || "soft") === firstEnforcement);
    return allSame ? firstEnforcement : "";
  };
  const groupEnforcement = getGroupEnforcement();

  // Während der Bearbeitung prüfen, ob ein anderer Group-Name identisch ist
  const isDuplicate = isEditing && groups.some(g => g.id !== group.id && g.name === editingGroupName.trim());

  /**
   * commitName:
   * - trimmt den Namen
   * - versucht, die Gruppe zu aktualisieren (updateGroup prüft auf Duplikate)
   * - schließt den Editmodus, wenn erfolgreich
   */
  const commitName = () => {
    const trimmed = editingGroupName.trim();
    if (trimmed && trimmed !== editingGroupName) setEditingGroupName(trimmed);
    if (updateGroup(group.id, { name: trimmed })) setEditingGroupId(null);
  };

  return (
    <div
      style={{
        ...style,
        position: style?.position,
        background,
        borderRadius: 6,
        padding: isDropTarget ? "0.75em" : "0.5em 0.5em 0.5em 2px",
        marginBottom: "1em",
        minHeight: isCollapsed ? "auto" : "100px",
        boxShadow: isOverlay ? "0 8px 24px rgba(0,0,0,0.2)" : style?.boxShadow,
        opacity: isOverlay ? 0.9 : 1,
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("application/x-zeitgeist")) {
          e.preventDefault();
          // Optional: Visual feedback handling could go here or via CSS :hover
        }
      }}
      onDrop={(e) => {
        // IMPORTANT: only accept drops that land on the group container itself.
        // If the drop originated from/landed on a child element (e.g. an expanded condition),
        // we must NOT open the pinned popup or create a new condition.
        if (e.target !== e.currentTarget) return;

        // If a nested dropzone (e.g. AlignDropZone / ConditionCard) already handled the drop,
        // don't create pinned conditions here.
        // Note: some browsers / DnD flows don't reliably preserve defaultPrevented across nested handlers,
        // so we also guard against drops originating from sync dropzones.
        if (e.defaultPrevented) return;

        const path = (e.nativeEvent as any)?.composedPath?.() as Array<EventTarget> | undefined;
        const cameFromSyncZone = Array.isArray(path)
          ? path.some((t) => (t as HTMLElement | null)?.getAttribute?.("data-align-dropzone") === "1")
          : false;
        if (cameFromSyncZone) return;

        e.preventDefault();
        const data = e.dataTransfer.getData("application/x-zeitgeist");
        if (!data) return;

        try {
          const payload = JSON.parse(data);
          if (payload.kind === "create-condition-from-dimension-point") {
            e.stopPropagation(); // Stop bubbling
          } else if (payload.kind === "create-condition-from-dimension-tag") {
            e.stopPropagation();
          } else if (payload.kind === "create-condition-from-dimension") {
            e.stopPropagation();
          }
        } catch (err) {
          console.error("Failed to parse drop data", err);
        }
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.2rem",
          marginBottom: "0.5em",
        }}
      >
        {/* Drag Handle für die Gruppe */}
        <div
          {...attributes}
          {...listeners}
          style={{ cursor: "grab", fontSize: "1.2em", padding: "4px 4px 4px 0" }}
          title="Drag to reorder · Drag out to remove (empty groups only)"
        >
          ☰
        </div>

        {/* Gruppenname: entweder im Edit-Modus oder als Label */}
        {isEditing ? (
          <div style={{ flex: 1 }}>
            <input
              autoFocus
              onFocus={(e) => e.target.select()}
              value={editingGroupName}
              onChange={(e) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS
                    .filter(c => raw.includes(c))
                    .map(c => c === "\n" ? "\u21b5" : c)
                    .join(", ");
                  setSpecialCharWarning(found);
                  setTimeout(() => setSpecialCharWarning(null), 2000);
                }
                setEditingGroupName(stripped);
              }}
              onBlur={commitName}
              onKeyDown={(e) => e.key === "Enter" && commitName()}
              style={{
                width: "100%",
                fontWeight: 600,
                padding: "2px 6px",
                border: isDuplicate ? "1px solid #d66" : undefined,
              }}
            />
            {isDuplicate && (
              <div style={{ color: "#d66", fontSize: "0.8em" }}>Name already in use</div>
            )}
            {specialCharWarning && (
              <span style={{ color: "#e55", fontSize: "0.8em" }}>
                "{specialCharWarning}" nicht erlaubt
              </span>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
            {/* Pfeil zum Collapse/Expand */}
            <span
              style={{ cursor: "pointer", paddingRight: "0.5em", userSelect: "none" }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isOverlay) toggleGroupCollapse(group.id);
              }}
            >
              {isCollapsed ? "▸" : "▾"}
            </span>
            {/* Name mit Doppelklick zum Editieren */}
            <span
              style={{ cursor: "pointer", fontWeight: 600, flex: 1 }}
              onDoubleClick={() => {
                if (isOverlay) return;
                setEditingGroupId(group.id);
                setEditingGroupName(group.name);
              }}
            >
              {group.name}
              {isCollapsed && (
                <>
                  <span style={{ fontSize: "0.8em", opacity: 0.7 }}>
                    {" "}
                    ({groupConds.length})
                  </span>
                  {/* Group Enforcement Dropdown - bei collapsed Gruppe */}
                  {!isOverlay && groupConds.length > 0 && (
                    <select
                      value={groupEnforcement}
                      onChange={(e) => {
                        const enforcement = e.target.value as "soft" | "hard" | "off";
                        if (!enforcement) return;
                        // Alle Conditions in dieser Gruppe auf einmal aktualisieren
                        const conditionIds = new Set(groupConds.map(c => c.id));
                        onChange(
                          conditions.map((c: ExtendedCondition) =>
                            conditionIds.has(c.id) ? { ...c, enforcement } : c
                          ),
                          groups
                        );
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        fontSize: "0.7em",
                        padding: "1px 2px",
                        marginLeft: "4px",
                        cursor: "pointer",
                        maxWidth: "45px",
                      }}
                      title="Set enforcement for all conditions in this group"
                    >
                      <option value="">Enf</option>
                      <option value="soft">soft</option>
                      <option value="hard">hard</option>
                      <option value="off">off</option>
                    </select>
                  )}
                </>
              )}
            </span>
          </div>
        )}

        {/* Fulfillment-Indikator der Gruppe */}
        {indicator && (
          <span
            style={{
              fontSize: "0.8em",
              fontWeight: "bold",
              color: indicator.color,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              lineHeight: 1.2,
              flexShrink: 0,
              marginLeft: "auto",
            }}
          >
            <span>{indicator.emoji}</span>
            <span>{indicator.pct}</span>
          </span>
        )}

      </div>

      {/* Inhalt der Gruppe, falls nicht eingeklappt */}
      {!isCollapsed && (
        <>
          {/* Hier werden die SortableConditionCards als children eingefügt */}
          {children}
          {!isOverlay && (
            <div style={{ display: "flex", gap: "8px", marginTop: "0.5em", flexWrap: "wrap" }}>
              {/* Group Enforcement Dropdown - innerhalb der Gruppe */}
              {groupConds.length > 0 && (
                <select
                  value={groupEnforcement}
                  onChange={(e) => {
                    const enforcement = e.target.value as "soft" | "hard" | "off";
                    if (!enforcement) return;
                    // Alle Conditions in dieser Gruppe auf einmal aktualisieren
                    const conditionIds = new Set(groupConds.map(c => c.id));
                    onChange(
                      conditions.map((c: ExtendedCondition) =>
                        conditionIds.has(c.id) ? { ...c, enforcement } : c
                      ),
                      groups
                    );
                  }}
                  style={{
                    fontSize: "0.85em",
                    padding: "4px 6px",
                    cursor: "pointer",
                  }}
                  title="Set enforcement for all conditions in this group"
                >
                  <option value="">Set Enforcement...</option>
                  <option value="soft">soft</option>
                  <option value="hard">hard</option>
                  <option value="off">off</option>
                </select>
              )}
              {/* Button zum Hinzufügen einer neuen Condition zu dieser Gruppe */}
              <button onClick={() => addCondition(group.id)}>
                + Condition
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * SortableGroup:
 * - Wrapper um GroupCard, macht die Gruppe selbst sortierbar per useSortable.
 * - Enthält die SortableConditionCards der Gruppe.
 */
function SortableGroup({ group, groupConds, key }: { group: ConditionGroup; groupConds: ExtendedCondition[]; key?: string | number }) {
  const { hoverGroupId } = useEditorContext();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    boxShadow: hoverGroupId === group.id ? "0 8px 22px rgba(0,0,0,0.12)" : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <GroupCard group={group} groupConds={groupConds} listeners={listeners} attributes={attributes}>
        {/* Innerhalb der Gruppe: SortableContext für die Conditions */}
        <SortableContext items={groupConds.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          {groupConds.map((c) => <SortableConditionCard key={c.id} cond={c} />)}
        </SortableContext>
      </GroupCard>
    </div>
  );
}

/* ---------- Main Component ---------- */

/**
 * ConditionEditor:
 * - Oberste Komponente für den Conditions-Editor mit Gruppen & Drag & Drop.
 * - Kapselt:
 *   - Kontext-Provider (EditorContext)
 *   - eigene Zustände (expandedId, editingGroupId, collapsedGroups, activeId, …)
 *   - DnD-Logik für Gruppen und Conditions
 *   - Normalisierung der Conditions (Default-Werte)
 *   - emitChange: zentrale Stelle, um onChange aufzurufen.
 */
export default function ConditionEditor(props: Props) {
  const { conditions, groups, onChange, isActive, toggle, userHue, mode, statsData, syntaxData } = props;

  // aktuell expandierte Condition
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // aktuell editierte Gruppe
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState("");

  // Set eingeklappter Gruppen (initial: alle Gruppen eingeklappt)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    () => new Set(groups.map(g => g.id))
  );

  // ID der Gruppe, über der gerade ein Drag-Hover stattfindet
  const [hoverGroupId, setHoverGroupId] = useState<string | null>(null);

  // ID des aktuell gezogenen Elements (Gruppe oder Condition)
  const [activeId, setActiveId] = useState<string | null>(null);
  const editorFrameRef = useRef<HTMLDivElement | null>(null);

  // DnD-Sensoren für den gesamten Editor (Gruppen + Conditions)
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Fix: Wenn Maus schnell aus dem Fenster geht, verliert dnd-kit den mouseup-Event.
  // Wir dispatchen einen synthetischen mouseup, damit dnd-kit den Drag sauber beendet.
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.relatedTarget === null) {
        document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
      }
    };
    window.addEventListener('mouseleave', handleMouseLeave);
    return () => window.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  // Wenn sich die Gruppen ändern (z. B. nach Import), werden alle Gruppen neu eingeklappt.
  useEffect(() => {
    setCollapsedGroups(new Set(groups.map(g => g.id)));
  }, [groups]);

  /**
   * conditionStatsMap:
   * - Map von ConditionName -> Statistik-Eintrag (aus statsData).
   * - wird für Fulfillment-Anzeigen etc. genutzt.
   */
  type ConditionStatEntry = {
    conditionName?: string;
    ConditionName?: string;
    fulfillment?: unknown;
    Fulfillment?: unknown;
    [k: string]: unknown;
  };

  const conditionStatsMap = useMemo(() => {
    const map = new Map<string, ConditionStatEntry>();

    const arr = (Array.isArray(statsData) ? statsData : []) as ConditionStatEntry[];

    arr.forEach((entry: ConditionStatEntry) => {
      const name = entry.conditionName || entry.ConditionName;
      if (!name) return;

      const normalized: ConditionStatEntry = {
        ...entry,
        ConditionName: name,
        Fulfillment: entry.fulfillment ?? entry.Fulfillment,
      };

      map.set(name, normalized);
    });

    return map;
  }, [statsData]);

  /**
   * conditionSyntaxMap:
   * - Map von ConditionName -> Syntax-Check-Eintrag (aus syntaxData).
   * - wird für Syntax-Error-Anzeigen etc. genutzt.
   */
  const conditionSyntaxMap = useMemo(() => {
    const map = new Map<string, any>();

    type SyntaxEntry = { conditionName?: string; syntax?: string; message?: string;[k: string]: any };
    const syntaxArray = (Array.isArray(syntaxData) ? syntaxData : []) as SyntaxEntry[];
    syntaxArray.forEach((entry) => {

      const name = entry?.conditionName;
      if (name) {
        // Backend liefert: { conditionName, syntax: "ok|warning|error", message: "..." }
        // Frontend erwartet: { SyntaxMessages: [...] }
        const messages = entry.message ? [entry.message] : [];
        const normalized = {
          conditionName: name,
          syntax: entry.syntax,
          SyntaxMessages: messages,
        };
        map.set(name, normalized);
      }
    });
    return map;
  }, [syntaxData]);

  /**
   * normalizedConditions:
   * - Conditions mit Default-Feldern aufgefüllt (groupId, dimrefs, patternType, patternValue).
   * - groupId fällt auf die erste vorhandene Gruppe zurück, falls leer.
   */
  const normalizedConditions = useMemo(() => {
    const firstGroupId = groups[0]?.id || "default";
    return conditions.map((c) => ({
      ...c,
      groupId: c.groupId || firstGroupId,
      dimrefs: c.dimrefs || [],
      patternType: c.patternType || "equal",
      patternValue: c.patternValue || "",
    }));
  }, [conditions, groups]);

  /**
   * emitChange:
   * - zentrale Stelle, um onChange aufzurufen.
   * - hält die fachliche Priorität über die Reihenfolge: Gruppen der Reihe nach,
   *   Conditions innerhalb der Gruppe in UI-Reihenfolge.
   */
  const emitChange = (newConds: ExtendedCondition[], newGroups: ConditionGroup[]) => {
    const ordered = newGroups.reduce(
      (acc: ExtendedCondition[], g: ConditionGroup) => [
        ...acc,
        ...newConds.filter((c: ExtendedCondition) => c.groupId === g.id),
      ],
      []
    );
    onChange(ordered, newGroups);

    // Activity-Ping
    notifyProjectActivity(props.projectId).catch(() => { });
  };

  /**
   * updateCondition:
   * - aktualisiert eine einzelne Condition anhand ihrer ID.
   */
  const updateCondition = (id: string, updates: Partial<ExtendedCondition>) => {
    emitChange(
      normalizedConditions.map((c: ExtendedCondition) =>
        c.id === id ? { ...c, ...updates } : c
      ),
      groups
    );
  };

  /**
   * deleteCondition:
   * - entfernt eine Condition anhand ihrer ID.
   * - falls diese Condition gerade expanded war, wird expandedId zurückgesetzt.
   */
  const deleteCondition = (id: string) => {
    emitChange(
      normalizedConditions.filter((c: ExtendedCondition) => c.id !== id),
      groups
    );
    if (expandedId === id) setExpandedId(null);
  };

  /**
   * updateGroup:
   * - aktualisiert eine Gruppe (z. B. Namen).
   * - validiert, dass der Name nicht schon von einer anderen Gruppe verwendet wird.
   * - gibt true zurück, wenn Update erfolgreich.
   */
  const updateGroup = (id: string, updates: Partial<ConditionGroup>) => {
    if (updates.name && groups.some((g) => g.id !== id && g.name === updates.name?.trim()))
      return false;
    emitChange(
      normalizedConditions,
      groups.map((g) => (g.id === id ? { ...g, ...updates } : g))
    );
    return true;
  };

  /**
   * deleteGroup:
   * - löscht eine Gruppe komplett.
   * - Conditions dieser Gruppe werden auf eine andere Gruppe umgehängt (fallback).
   */
  /**
   * deleteGroup:
   * - löscht eine Gruppe komplett.
   * - DARF NUR GELÖSCHT WERDEN, WENN LEER (keine Conditions).
   */
  const deleteGroup = (id: string) => {
    // Safety Check: Sind noch Conditions in dieser Gruppe?
    const hasConditions = normalizedConditions.some((c) => c.groupId === id);
    if (hasConditions) return;

    const newGroups = groups.filter((g) => g.id !== id);
    // Conditions müssten leer sein, daher keine Umhängung nötig.
    // Sicherheitshalber filtern wir Conditions dieser Gruppe raus, falls doch welche da wären (was wir oben verhindern).
    const newConds = normalizedConditions.filter((c) => c.groupId !== id);

    emitChange(newConds, newGroups);
  };

  /**
   * addCondition:
   * - fügt eine neue Condition in einer bestimmten Gruppe hinzu.
   * - Name, Typ etc. mit Standardwerten vorbelegt.
   * - neue Condition wird direkt expandiert.
   */
  const addCondition = (groupId: string) => {
    const newCond: ExtendedCondition = {
      id: Date.now().toString(),
      name: "Condition" + (conditions.length + 1),
      type: "count",
      dimrefs: [],
      patternType: "equal",
      patternValue: "",
      groupId,
    };
    emitChange([...normalizedConditions, newCond], groups);
    setExpandedId(newCond.id);
  };

  /**
   * handleDragEnd:
   * - zentrale DnD-Logik für Gruppen und Conditions.
   * - unterscheidet:
   *   - Drag einer Gruppe -> Reorder der groups
   *   - Drag einer Condition -> Verschieben innerhalb/zwischen Gruppen.
   */
  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    setHoverGroupId(null);

    const { active, over } = event;
    const activeId = String(active.id);
    const draggedCond = normalizedConditions.find((c) => c.id === activeId);
    const draggedGroup = groups.find((g) => g.id === activeId);

    // Fallback für "Drag out of admin/editor area":
    // closestCenter kann selbst außerhalb noch ein over liefern.
    // Gelöscht wird nur, wenn das Drag-Element KOMPLETT außerhalb liegt
    // (keine Überlappung mehr mit dem Editor-Rechteck).
    const finalRect = active.rect.current.translated ?? active.rect.current.initial;
    const editorRect = editorFrameRef.current?.getBoundingClientRect();
    const droppedCompletelyOutsideEditor =
      !!finalRect &&
      !!editorRect &&
      !!(draggedCond || draggedGroup) &&
      (finalRect.right <= editorRect.left ||
        finalRect.left >= editorRect.right ||
        finalRect.bottom <= editorRect.top ||
        finalRect.top >= editorRect.bottom);
    if (droppedCompletelyOutsideEditor) {
      if (draggedCond) deleteCondition(activeId);
      if (draggedGroup) deleteGroup(activeId);
      return;
    }

    // Kein gültiges Drop-Target, aber noch (teilweise) im Editor-Bereich:
    // nicht löschen.
    if (!over) {
      return;
    }

    const overId = String(over.id);

    // 1) Drag einer Gruppe
    if (draggedGroup) {
      const overGroup = groups.find((g) => g.id === overId);
      if (overGroup) {
        const oldIdx = groups.findIndex((g) => g.id === activeId);
        const newIdx = groups.findIndex((g) => g.id === overId);
        emitChange(normalizedConditions, arrayMove(groups, oldIdx, newIdx));
      }
      return;
    }

    // 2) Drag einer Condition
    if (!draggedCond) return;
    const overGroup = groups.find((g) => g.id === overId);
    const overCond = normalizedConditions.find((c) => c.id === overId);
    const targetGroupId = overGroup?.id ?? overCond?.groupId;

    if (!targetGroupId) return;

    // Wenn Zielgruppe eingeklappt ist, beim Drop automatisch expandieren
    if (collapsedGroups.has(targetGroupId)) {
      setCollapsedGroups((prev: Set<string>) => {
        const next = new Set(prev);
        next.delete(targetGroupId);
        return next;
      });
    }

    // Conditions, die aktuell in der Zielgruppe sind
    const targetConds = normalizedConditions.filter((c) => c.groupId === targetGroupId);

    // Zielindex innerhalb der Zielgruppe bestimmen
    let targetIndex = targetConds.length;
    if (overCond) {
      const idx = targetConds.findIndex((c) => c.id === overId);
      if (idx !== -1) targetIndex = idx;
    }

    // Alte Liste ohne die gezogene Condition
    const newConds = normalizedConditions.filter((c) => c.id !== activeId);
    const movedCond = { ...draggedCond, groupId: targetGroupId };

    // Wir rekonstruiere die Liste gruppenweise mit neuer Reihenfolge
    const grouped = new Map<string, ExtendedCondition[]>();
    groups.forEach((g) => grouped.set(g.id, []));
    normalizedConditions.forEach((c) => {
      if (c.id === activeId) return; // überspringe gezogene Condition
      grouped.get(c.groupId)?.push(c);
    });

    // In der Zielgruppe an der berechneten Position einfügen
    const targetList = grouped.get(targetGroupId)!;
    targetList.splice(targetIndex, 0, movedCond);

    // finale Liste in Gruppen-Reihenfolge zusammenbauen
    const finalConds = groups.reduce(
      (acc: ExtendedCondition[], g: ConditionGroup) => {
        const list = grouped.get(g.id);
        return list ? [...acc, ...list] : acc;
      },
      []
    );

    emitChange(finalConds, groups);
  };

  // aktuell gezogene Gruppe oder Condition für DragOverlay
  const activeGroup = activeId ? groups.find((g) => g.id === activeId) : null;
  const activeCondition = activeId
    ? normalizedConditions.find((c) => c.id === activeId)
    : null;

  return (
    <EditorContext.Provider
      value={{
        ...props,
        conditionStatsMap,
        conditionSyntaxMap,
        updateCondition,
        deleteCondition,
        updateGroup,
        deleteGroup,
        addCondition,
        expandedId,
        setExpandedId,
        editingGroupId,
        setEditingGroupId,
        editingGroupName,
        setEditingGroupName,
        collapsedGroups,
        toggleGroupCollapse: (id: string) =>
          setCollapsedGroups((prev: Set<string>) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
          }),
        hoverGroupId,
      }}
    >
      {/* Rahmen des Editors */}
      <div
        ref={editorFrameRef}
        style={{
          marginTop: "1em",
          marginBottom: "1em",
          padding: "0.5em",
          borderRadius: 8,
          background: getBaseColor(2, "section", userHue, mode),
        }}
      >
        {/* Header-Leiste des Editors (auf- und zuklappbar) */}
        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={toggle}
        >
          <strong>{isActive ? "▾" : "▸"} Conditions</strong>
        </div>

        {/* Innerer Inhalt nur, wenn Editor aktiv/aufgeklappt ist */}
        {isActive && (
          <div style={{ marginTop: "0.5em" }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={(e: DragStartEvent) => {
                setActiveId(String(e.active.id));
                setExpandedId(null);
              }}
              onDragOver={(e: DragOverEvent) => {
                const { over } = e;
                if (!over) return setHoverGroupId(null);
                const overId = String(over.id);

                // Hover über Gruppen
                const group = groups.find((g) => g.id === overId);
                if (group) return setHoverGroupId(group.id);

                // Hover über Conditions -> deren Gruppe markieren
                const cond = normalizedConditions.find(
                  (c: ExtendedCondition) => c.id === overId
                );
                setHoverGroupId(cond?.groupId ?? null);
              }}
              onDragEnd={handleDragEnd}
            >
              {/* SortableContext für Gruppen */}
              <SortableContext items={groups.map((g) => g.id)} strategy={verticalListSortingStrategy}>
                {groups.map((group) => (
                  <SortableGroup
                    key={group.id}
                    group={group}
                    groupConds={normalizedConditions.filter(
                      (c: ExtendedCondition) => c.groupId === group.id
                    )}
                  />
                ))}
              </SortableContext>

              {/* DragOverlay: "Ghost" Element während des Drags */}
              <DragOverlay>
                {activeGroup ? (
                  <GroupCard
                    group={activeGroup}
                    groupConds={normalizedConditions.filter(
                      (c) => c.groupId === activeGroup.id
                    )}
                    isOverlay
                  />
                ) : activeCondition ? (
                  <ConditionCard cond={activeCondition} isOverlay />
                ) : null}
              </DragOverlay>
            </DndContext>

            {/* Button zum Hinzufügen einer neuen Gruppe */}
            <button
              onClick={() => {
                const name = "Group" + (groups.length + 1);
                const newGroup = { id: Date.now().toString(), name, enabled: false };
                emitChange(normalizedConditions, [...groups, newGroup]);
                setEditingGroupId(newGroup.id);
                setEditingGroupName(name);
              }}
              style={{ marginTop: "1em", padding: "0.4em 0.9em" }}
            >
              + Group
            </button>
          </div>
        )}
      </div>
    </EditorContext.Provider>
  );
}
